import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useOutletContext } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
//icons
import { RiDeleteBinLine } from 'react-icons/ri';
//api
import { deleteSubscription, exportSubscriptions } from 'api/company';
import { getAllListSubscription } from 'api/pathForHook/company';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
//helpers
import { formatDateToSpanish } from 'helpers';
//components
import Button from 'components/backoffice/buttonCustom';
import CustomTable from 'components/backoffice/customTable';
import Title from 'components/backoffice/texts/title';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SearchTable from 'components/backoffice/table/SearchTable';
import { usePermissions } from 'components/backoffice/PermissionsContext';

const EmailSubscriptions = () => {
  const userPermissions = usePermissions();
  const [listSubscriptions, setListSubscriptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [idItem, setIdItem] = useState({});
  const [responseInfo, setResponseInfo] = useState({});
  const [btnExportEnabled, setBtnExportEnabled] = useState(true);
  const [createdDate, setCreatedDate] = useState('');
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext();

  const colsSuscriptions = [
    'ID',
    t('correoelectronico'),
    t('creationdate'),
    t('actions')
  ];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getListSubscriptions = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        email: inputValue,
        fields: 'id,email,created'
      }
      await fetchData(getAllListSubscription(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setTitle(t('suscripciones'))
    return () => {
      setTitle('');
    };
  }, [setTitle])


  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    setListSubscriptions((prevListSubscriptions) => {
      if (JSON.stringify(prevListSubscriptions) !== JSON.stringify(results)) {
        return results !== null ? results : null;
      }
      return prevListSubscriptions;
    });

    setResponseInfo((prevResponseInfo) => {
      const newResponseInfo = { errors, totalPages, noResults };
      if (JSON.stringify(prevResponseInfo) !== JSON.stringify(newResponseInfo)) {
        return newResponseInfo;
      }
      return prevResponseInfo;
    });
  }, [stateData])


  useEffect(() => {
    if (page > 1 && (inputValue === '' || inputValue !== '')) {
      setPage(1)
    }
  }, [inputValue])

  useEffect(() => {
    getListSubscriptions();
  }, [page, inputValue]);

  const handleClear = () => {
    setInputValue('');
    setCreatedDate('');
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, totalPages: responseInfo?.totalPages }));
  };

  const handleExportSubscriptions = async () => {
    setBtnExportEnabled(false);

    const payload = {};
    if (createdDate) {
      payload.created_date = createdDate;
    }
    try {
      const { status, data } = await exportSubscriptions(payload);
      if (status === 200 && data) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `subscriptions_${new Date().toISOString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.error(error);
      toast.error(t('mensajeerror'));
    } finally {
      setBtnExportEnabled(true);
    }
  };

  const handleDelete = useCallback(async () => {
    const { status } = await deleteSubscription(idItem.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      getListSubscriptions();
    }
    setShowModalDelete(false);
  }, [showModalDelete]);

  const openModalDelete = (object) => {
    setShowModalDelete(true);
    setIdItem(object);
  };

  return (
    <div>
      <Title text={t('suscripciones')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col lg={8} md={12} xs={12} className="d-flex text-start align-items-center gap-2">
          <SearchTable
            value={inputValue}
            handleClear={handleClear}
            placeholder={t('buscarcorreo')}
            handleSearch={setInputValue}
            handleKeyDown={true}
          />
        </Col>
        <Col lg={4} md={12} xs={12} className="d-flex text-end justify-content-end align-items-center pt-3 pt-lg-0 gap-2">
          {userPermissions.includes('suscripcionboletin.__view') && (
            <>
              <input
                type="date"
                className="form-control emailSubscriptionsCreatedDate"
                value={createdDate}
                onChange={(e) => setCreatedDate(e.target.value)}
                aria-label={t('creationdate')}
              />
              <Button
                variant="secondary"
                color="primary"
                onClick={handleExportSubscriptions}
                disabled={!btnExportEnabled}
              >
                {t('export')}
              </Button>
            </>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsSuscriptions}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {listSubscriptions &&
            listSubscriptions.map((listSubscription) => {
              return (
                <tr key={listSubscription.id}>
                  <td>{`#${listSubscription.id}`}</td>
                  <td>{listSubscription.email}</td>
                  <td>{formatDateToSpanish(listSubscription.created)}</td>
                  <td className="text-end">
                    {userPermissions.includes('suscripcionboletin.__delete') && (
                      <RiDeleteBinLine
                        className="mx-3 style-icon-delete"
                        size={20}
                        onClick={() => openModalDelete(listSubscription)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showModalDelete}
        setOpenModal={setShowModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deletesubscription')}
        beforeTitle={t('suscripciones')}
      />
    </div>
  );
};

export default EmailSubscriptions;
